import { ResponseError } from '@jucy/rentals-api-client';
import { attempt } from 'lodash';
import VError from 'verror';

export const getErrorMetaData = (error?: Error): Record<string, unknown> => {
    if (error instanceof Error) {
        const cause = VError.cause(error);
        const name = cause?.name || error.name || '';
        return {
            info: VError.info(error),
            stack: VError.fullStack(error),
            cause,
            name,
        };
    }
    return {
        name: 'Unknown',
    };
};

const isResponse = (error: unknown): error is Response =>
    typeof (error as Response)?.formData === 'function' &&
    typeof (error as Response)?.json === 'function' &&
    typeof (error as Response)?.text === 'function';

const isRentalsApiResponseError = (error: unknown): error is ResponseError => (error as ResponseError)?.name === 'ResponseError';

export const hasResponse = (e: unknown): e is { response: Response } => isResponse((e as { response: Response })?.response);

const createResponseError = async (response: Response, cause?: unknown): Promise<VError> => {
    const body = await response.text().catch(() => null);
    const bodyJson = body ? ((await attempt(JSON.parse, body)) as { message?: string; error_description?: string }) : undefined;

    const message =
        bodyJson?.message || bodyJson?.error_description || `[HTTP ${response.status}]: ${response.statusText || ''}${body ? ` - ${body}` : ''}`;
    let resolvedCause = cause instanceof Error ? cause : undefined;
    if (cause && !resolvedCause) {
        resolvedCause = new Error(cause as string);
    }
    const options: VError.Options = {
        name: 'Response error',
        cause: resolvedCause,
        info: {
            status: response.status,
            request: {
                url: response.url,
            },
            response: {
                ...response,
                body: bodyJson || body,
            },
        },
    };
    return new VError(options, message);
};
export const resolveError = async (error: Response | Error): Promise<VError | Error> => {
    if (isRentalsApiResponseError(error)) {
        return error;
    }
    if (hasResponse(error)) {
        return createResponseError(error.response, error);
    }
    if (error instanceof VError) {
        return error;
    }
    if (error instanceof Error) {
        return error;
    }
    return createResponseError(error);
};

export const resolveErrorMessage = async (e: Response | Error | string): Promise<string> => {
    if (typeof e === 'string') {
        return e;
    }
    const resolvedError = await resolveError(e);
    return resolvedError.message;
};
export default resolveErrorMessage;

type MessageResponse = { code: number; message: string; error: string };
export const extractCode = (input: Error | Response | unknown): number => {
    if ((input as Response).status) {
        return (input as Response).status;
    } else if ((input as { statusCode: number }).statusCode) {
        return (input as { statusCode: number }).statusCode;
    } else if ((input as { code: number }).code) {
        return (input as { code: number }).code;
    }
    return 500;
};

export const parseError = async (input: Error | Response | unknown): Promise<MessageResponse> => {
    const result = { code: extractCode(input) || 500, message: '', error: 'Internal Server Error' };
    const response = hasResponse(input) ? input.response : isResponse(input) ? input : undefined;
    if (response) {
        const data = (await response.json()) as { error: string; message: string };
        result.error = data.error;
        result.message = data.message;
    }
    if (!result.message) {
        result.message = input instanceof Error ? input.message : JSON.stringify(input);
    }
    return result;
};
